import {
  gaussJordanElimination,
  type Matrix,
  type Vector,
} from "./gaussJordanElimination";

/**
 * Solves system of equations using Least Squares method.
 * @see https://imagemagick.org/api/MagickCore/matrix_8c_source.html#l00829 LeastSquaresAddTerms() at
 * ImageMagick source.
 */
export class LeastSquares {
  /**
   * Coefficients of equations to solve.
   *
   * @private
   */
  private readonly coefficients: Matrix;

  /**
   * Results of equations to solve.
   *
   * @private
   */
  private readonly results: Matrix;

  /**
   * LeastSquares constructor.
   *
   * @param rank The rank or size of the dimensions of the square matrix.
   * Also, the length of vectors, and number of terms being added.
   * @param numVectors Number of result vectors, and number of results being
   * added.  Also represents the number of separable systems of equations
   * that is being solved.
   */
  constructor(rank: number, numVectors = 1) {
    this.coefficients = [];
    this.results = [];

    for (let i = 0; i < rank; i++) {
      this.coefficients.push(new Array(rank).fill(0));
    }

    for (let i = 0; i < numVectors; i++) {
      this.results.push(new Array(rank).fill(0));
    }
  }

  /**
   * Adds one set of terms and associate results to the given matrix and vectors for solving using least-squares
   * function fitting.
   *
   * @param terms The pre-calculated terms (without any coefficient weights) that forms the equation being added.
   * @param results The result(s) that should be generated from the given terms weighted by the yet-to-be-solved
   * coefficients.
   *
   * @see https://imagemagick.org/api/MagickCore/matrix_8c_source.html#l00829 LeastSquaresAddTerms() at
   * ImageMagick source.
   */
  addTerms(terms: Vector, results: Vector) {
    const rank = this.coefficients.length;

    for (let j = 0; j < rank; j++) {
      for (let i = 0; i < rank; i++) {
        this.coefficients[i][j] += terms[i] * terms[j];
      }

      for (let i = 0; i < this.results.length; i++) {
        this.results[i][j] += results[i] * terms[j];
      }
    }

    return this;
  }

  /**
   * Returns equations solving results matrix
   */
  solve(): Matrix {
    return gaussJordanElimination(this.coefficients, this.results);
  }
}
